import { BankOutlined, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import { Button, Card, ConfigProvider, DatePicker, Segmented, Select, Space, Spin, Table } from 'antd';
import frFR from "antd/locale/fr_FR";
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import axiosInstance from '../../../variables/api/axiosInstance';
import { LOGGED_USER } from '../../../variables/constants';
import { NAVY, drawEtatHeaderFooter, finalizeEtatPdf, loadEntete, loadLogos } from '../../stats/sinistre/etatsSinistresFunctions';
import { displayNumbers } from '../../../variables/numbersFunctions';

const IS_POOL = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";

const fmt = (n) => displayNumbers(Math.trunc(Number(n) || 0));
// Formatage à espace ASCII (U+0020) pour le PDF : la police jsPDF (helvetica) ne rend pas l'espace
// insécable étroite (U+202F/U+00A0) utilisée par displayNumbers → « 6 / 0 0 » au lieu de « 6 000 ».
const fmtPdf = (n) => `${Math.trunc(Number(n) || 0)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

// Récupère TOUTES les lignes d'un endpoint paginé (Spring plafonne size à ~2000) en bouclant les
// pages — indispensable pour des totaux fiscaux exacts sur un mois à fort volume.
const fetchAllPaginated = async (url, params) => {
    const size = 1000;
    let page = 0, totalPages = 1;
    const all = [];
    do {
        // eslint-disable-next-line no-await-in-loop
        const res = await axiosInstance.get(url, { params: { ...params, page, size } });
        const data = res?.data;
        const content = data?.content || (Array.isArray(data) ? data : []);
        all.push(...content);
        totalPages = data?.totalPages ?? 1;
        page += 1;
    } while (page < totalPages && page < 1000);
    return all;
};

/**
 * Écran de fiscalité générique : sélecteur de MOIS (ou d'ANNÉE) + bloc récap (totaux à reverser) +
 * listing détaillé + exports PDF (en-tête PoolIns) et Excel. Paramétré par écran (TCA, honoraires, commissions).
 *
 * Props :
 *  - title, subtitle : intitulés
 *  - fetchRows(startISO, endISO, compagnie) : Promise<rows[]>
 *  - kpis(rows) : [{ label, value(number), money?, highlight?, sub? }]
 *  - columns : colonnes AntD du détail
 *  - exportColumns : [{ header, accessor(r), money?, align? }] pour PDF + Excel (+ ligne TOTAL)
 *  - groupRecap(rows) : (optionnel) [{ label, count, total }] pour un récap groupé (ex. par expert)
 *  - groupRecapTitle : titre du récap groupé
 *  - groupRecaps : (optionnel) [{ title, labelHeader, compute(rows) }] — plusieurs récaps groupés
 *      (ex. par bénéficiaire ET par taux d'impôt). Prend le pas sur groupRecap si fourni.
 *  - enableAnnual : (optionnel) active la bascule « Mensuel / Annuel » (récap des KPI monétaires mois par mois).
 *  - pdfTitle, fileBase
 */
const FiscaliteScreen = ({
    title, subtitle, fetchRows, kpis, columns, exportColumns,
    groupRecap, groupRecapTitle, groupRecaps, enableAnnual, pdfTitle, fileBase,
}) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mois, setMois] = useState(dayjs());
    const [compagnie, setCompagnie] = useState(null);
    const [compagnies, setCompagnies] = useState([]);

    // Vue annuelle (récap mois par mois)
    const [vue, setVue] = useState('mois'); // 'mois' | 'annee'
    const [annee, setAnnee] = useState(dayjs());
    const [moisData, setMoisData] = useState([]); // [{ index, label, count, kpis }]
    const [loadingAnnee, setLoadingAnnee] = useState(false);

    const charger = (m, comp) => {
        setLoading(true);
        const start = m.startOf('month').format('YYYY-MM-DD');
        const end = m.endOf('month').format('YYYY-MM-DD');
        Promise.resolve(fetchRows(start, end, comp))
            .then((data) => setRows(Array.isArray(data) ? data : []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    };

    // Charge les 12 mois d'une année en parallèle et calcule les KPI par mois.
    const chargerAnnee = (y, comp) => {
        setLoadingAnnee(true);
        const tasks = Array.from({ length: 12 }, (_, mIdx) => {
            const mm = y.month(mIdx);
            const s = mm.startOf('month').format('YYYY-MM-DD');
            const e = mm.endOf('month').format('YYYY-MM-DD');
            return Promise.resolve(fetchRows(s, e, comp))
                .then((d) => ({ index: mIdx, label: mm.format('MMMM'), rows: Array.isArray(d) ? d : [] }))
                .catch(() => ({ index: mIdx, label: mm.format('MMMM'), rows: [] }));
        });
        Promise.all(tasks)
            .then((res) => {
                res.sort((a, b) => a.index - b.index);
                setMoisData(res.map((r) => ({ index: r.index, label: r.label, count: r.rows.length, kpis: kpis(r.rows) || [] })));
            })
            .finally(() => setLoadingAnnee(false));
    };

    useEffect(() => {
        charger(mois, compagnie);
        // Sélecteur de compagnie réservé au Pool (compagnies + Pool lui-même).
        if (IS_POOL) {
            axiosInstance.get('/api/organisme/all/assurances-pool')
                .then((r) => setCompagnies(r.data || []))
                .catch(() => setCompagnies([]));
        }
        /* eslint-disable-next-line */
    }, []);

    // Recharge la vue active (mensuelle ou annuelle) — ex. après changement de compagnie.
    const rechargerVueActive = (comp) => {
        if (vue === 'annee') chargerAnnee(annee, comp);
        else charger(mois, comp);
    };

    const onChangeVue = (v) => {
        setVue(v);
        if (v === 'annee') chargerAnnee(annee, compagnie);
        else charger(mois, compagnie);
    };

    const kpiList = kpis(rows) || [];
    // Récaps groupés (compat : groupRecap unique → tableau)
    const allGroupRecaps = (groupRecaps && groupRecaps.length)
        ? groupRecaps
        : (groupRecap
            ? [{ title: groupRecapTitle, labelHeader: groupRecapTitle?.toLowerCase().includes('expert') ? 'Expert' : 'Bénéficiaire', compute: groupRecap }]
            : []);
    const moisLabel = mois.format('MMMM YYYY');
    const suffixe = mois.format('YYYY-MM');

    // KPI monétaires (colonnes de la vue annuelle)
    const moneyKpiDefs = (moisData[0]?.kpis || kpis([]) || []).filter((k) => k.money);
    const valMoisKpi = (row, label) => row.kpis?.find((k) => k.label === label)?.value || 0;
    const totalKpiAnnee = (label) => moisData.reduce((a, r) => a + valMoisKpi(r, label), 0);

    // Vue annuelle = une seule colonne « Montant » : le KPI monétaire en exergue (« à reverser »),
    // à défaut le dernier KPI monétaire. En-tête façon état officiel (compagnie / titre / année).
    const montantDef = moneyKpiDefs.find((k) => k.highlight) || moneyKpiDefs[moneyKpiDefs.length - 1];
    const montantLabel = montantDef?.label || 'Montant';
    const valMois = (r) => (montantDef ? valMoisKpi(r, montantDef.label) : 0);
    const totalMontantAnnee = montantDef ? totalKpiAnnee(montantDef.label) : 0;
    const compagnieNom = compagnie
        ? (compagnies.find((o) => (o.code || o.codeOrganisme) === compagnie)?.raisonSociale || compagnie)
        : 'Toutes les compagnies';

    // ── Exports vue mensuelle ────────────────────────────────────────────────────
    const exporterExcel = () => {
        if (!rows.length) return;
        const lignes = rows.map((r, i) => {
            const o = { "N°": i + 1 };
            exportColumns.forEach((c) => { o[c.header] = c.money ? Math.trunc(Number(c.accessor(r)) || 0) : (c.accessor(r) ?? ''); });
            return o;
        });
        const totalRow = { "N°": '' };
        exportColumns.forEach((c, idx) => {
            if (idx === 0) { totalRow[c.header] = 'TOTAL'; return; }
            totalRow[c.header] = c.money ? Math.trunc(rows.reduce((a, r) => a + (Number(c.accessor(r)) || 0), 0)) : '';
        });
        lignes.push(totalRow);
        const ws = XLSX.utils.json_to_sheet(lignes);
        ws["!cols"] = [{ wch: 5 }, ...exportColumns.map((c) => ({ wch: c.money ? 16 : 22 }))];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Fiscalité");
        XLSX.writeFile(wb, `${fileBase}_${suffixe}.xlsx`);
    };

    const exporterPDF = async () => {
        if (!rows.length) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const formattedNow = new Date().toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

        const headers = ['N°', ...exportColumns.map((c) => c.header)];
        const body = rows.map((r, i) => ([
            i + 1,
            ...exportColumns.map((c) => (c.money ? fmtPdf(c.accessor(r)) : (c.accessor(r) ?? '—'))),
        ]));
        const totalCells = ['', { content: 'TOTAL', styles: { halign: 'right', fontStyle: 'bold' } }];
        exportColumns.slice(1).forEach((c) => {
            totalCells.push(c.money
                ? { content: fmtPdf(rows.reduce((a, r) => a + (Number(c.accessor(r)) || 0), 0)), styles: { halign: 'right', fontStyle: 'bold' } }
                : '');
        });
        body.push(totalCells);

        autoTable(doc, {
            head: [headers], body, theme: 'grid',
            styles: { fontSize: 7.5, cellPadding: 1.5, lineWidth: 0.1, lineColor: [215, 215, 225], textColor: [45, 45, 45], overflow: 'linebreak', valign: 'middle' },
            headStyles: { fillColor: NAVY, textColor: 255, fontSize: 7.5, fontStyle: 'bold', lineWidth: 0.1, lineColor: NAVY },
            alternateRowStyles: { fillColor: [246, 247, 251] },
            columnStyles: exportColumns.reduce((acc, c, idx) => { acc[idx + 1] = { halign: c.align || (c.money ? 'right' : 'left') }; return acc; }, { 0: { cellWidth: 10, halign: 'center' } }),
            margin: { top: 32, bottom: 22, left: 14, right: 14 },
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: `${pdfTitle} — ${moisLabel.toUpperCase()}`,
                subtitle: `${rows.length} ligne(s)`,
                enteteData, logos, formattedNow,
            }),
        });
        finalizeEtatPdf(doc);
        doc.save(`${fileBase}_${suffixe}.pdf`);
    };

    // ── Exports vue annuelle (mois par mois : Mois | Montant + en-tête compagnie/année) ──
    const exporterAnneeExcel = () => {
        if (!moisData.length) return;
        const aoa = [
            ['Nom compagnie :', compagnieNom],
            [title],
            ['Année :', annee.format('YYYY')],
            [],
            ['Mois', 'Montant'],
            ...moisData.map((r) => [r.label, Math.trunc(valMois(r))]),
            ['TOTAL année', Math.trunc(totalMontantAnnee)],
        ];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws["!cols"] = [{ wch: 18 }, { wch: 18 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Récap annuel");
        XLSX.writeFile(wb, `${fileBase}_annuel_${annee.format('YYYY')}.xlsx`);
    };

    const exporterAnneePDF = async () => {
        if (!moisData.length) return;
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const formattedNow = new Date().toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

        const body = moisData.map((r) => ([
            { content: r.label, styles: { halign: 'left' } },
            fmtPdf(valMois(r)),
        ]));
        body.push([
            { content: 'TOTAL année', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: fmtPdf(totalMontantAnnee), styles: { halign: 'right', fontStyle: 'bold' } },
        ]);

        autoTable(doc, {
            head: [['Mois', 'Montant (FCFA)']], body, theme: 'grid',
            styles: { fontSize: 9, cellPadding: 2.2, lineWidth: 0.1, lineColor: [215, 215, 225], textColor: [45, 45, 45], valign: 'middle' },
            headStyles: { fillColor: NAVY, textColor: 255, fontSize: 9, fontStyle: 'bold', lineWidth: 0.1, lineColor: NAVY },
            alternateRowStyles: { fillColor: [246, 247, 251] },
            columnStyles: { 0: { halign: 'left', cellWidth: 60 }, 1: { halign: 'right' } },
            margin: { top: 40, bottom: 22, left: 14, right: 14 },
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: `${pdfTitle} — RÉCAP ANNUEL ${annee.format('YYYY')}`,
                subtitle: `Compagnie : ${compagnieNom}`,
                enteteData, logos, formattedNow,
            }),
        });
        finalizeEtatPdf(doc);
        doc.save(`${fileBase}_annuel_${annee.format('YYYY')}.pdf`);
    };

    const annuel = vue === 'annee';

    return (
        <ConfigProvider locale={frFR}>
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                    <SideBar currentMenu={"COMPTABILITE"} />
                    <div className="layout-page">
                        <NavBar />
                        <div className="content-wrapper">
                            <div className="container-xxl flex-grow-1 container-p-y">

                                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap" style={{ gap: 12 }}>
                                    <div>
                                        <h4 className="mb-0"><BankOutlined style={{ marginRight: 8 }} />{title}</h4>
                                        {subtitle && <div style={{ color: '#6b7280', fontSize: 13 }}>{subtitle}</div>}
                                    </div>
                                    <Space wrap>
                                        {enableAnnual && (
                                            <Segmented
                                                value={vue}
                                                onChange={onChangeVue}
                                                options={[{ label: 'Mensuel', value: 'mois' }, { label: 'Annuel', value: 'annee' }]}
                                            />
                                        )}
                                        {IS_POOL && (
                                            <Select
                                                style={{ minWidth: 230 }}
                                                value={compagnie}
                                                allowClear
                                                showSearch
                                                optionFilterProp="label"
                                                placeholder="Toutes les compagnies"
                                                onChange={(v) => { const c = v || null; setCompagnie(c); rechargerVueActive(c); }}
                                                options={[
                                                    { value: null, label: 'Toutes les compagnies' },
                                                    ...compagnies.map((o) => ({ value: o.code || o.codeOrganisme, label: o.raisonSociale || o.nom || o.code })),
                                                ]}
                                            />
                                        )}
                                        {annuel ? (
                                            <>
                                                <DatePicker picker="year" value={annee} allowClear={false} format="YYYY"
                                                    onChange={(d) => { const y = d || dayjs(); setAnnee(y); chargerAnnee(y, compagnie); }} />
                                                <Button danger icon={<FilePdfOutlined />} onClick={exporterAnneePDF} disabled={!moisData.length}>PDF</Button>
                                                <Button type="primary" icon={<FileExcelOutlined />} onClick={exporterAnneeExcel} disabled={!moisData.length}>Excel</Button>
                                            </>
                                        ) : (
                                            <>
                                                <DatePicker picker="month" value={mois} allowClear={false} format="MMMM YYYY"
                                                    onChange={(d) => { const m = d || dayjs(); setMois(m); charger(m, compagnie); }} />
                                                <Button danger icon={<FilePdfOutlined />} onClick={exporterPDF} disabled={!rows.length}>PDF</Button>
                                                <Button type="primary" icon={<FileExcelOutlined />} onClick={exporterExcel} disabled={!rows.length}>Excel</Button>
                                            </>
                                        )}
                                    </Space>
                                </div>

                                {annuel ? (
                                    /* ═══════════════ VUE ANNUELLE (mois par mois) ═══════════════ */
                                    <div className="card" style={{ maxWidth: 640 }}>
                                        <div className="card-body">
                                            {/* En-tête façon état officiel */}
                                            <div style={{ marginBottom: 14, lineHeight: 1.7 }}>
                                                <div><b>Nom compagnie :</b> {compagnieNom}</div>
                                                <div style={{ fontWeight: 700, fontSize: 15, color: '#242166' }}>{title}</div>
                                                <div><b>Année :</b> {annee.format('YYYY')}</div>
                                            </div>
                                            <Spin spinning={loadingAnnee}>
                                                <Table
                                                    dataSource={moisData}
                                                    rowKey="index"
                                                    pagination={false}
                                                    size="small"
                                                    locale={{ emptyText: `Aucune donnée pour ${annee.format('YYYY')}` }}
                                                    columns={[
                                                        { title: 'Mois', dataIndex: 'label', render: (v) => <span style={{ textTransform: 'capitalize' }}>{v}</span> },
                                                        {
                                                            title: `Montant (FCFA)`, key: 'montant', align: 'right', width: 200,
                                                            render: (_, r) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(valMois(r))}</span>,
                                                        },
                                                    ]}
                                                    summary={() => (
                                                        <Table.Summary.Row style={{ fontWeight: 700, background: '#fafafc' }}>
                                                            <Table.Summary.Cell index={0} align="right"><b>TOTAL année</b></Table.Summary.Cell>
                                                            <Table.Summary.Cell index={1} align="right"><b style={{ color: '#242166' }}>{fmt(totalMontantAnnee)}</b></Table.Summary.Cell>
                                                        </Table.Summary.Row>
                                                    )}
                                                />
                                            </Spin>
                                        </div>
                                    </div>
                                ) : (
                                    /* ═══════════════ VUE MENSUELLE ═══════════════ */
                                    <>
                                        {/* Récap (totaux à reverser) */}
                                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(kpiList.length || 1, 4)}, 1fr)`, gap: 12, marginBottom: 16 }}>
                                            {kpiList.map((k, i) => (
                                                <div key={i} className="card" style={k.highlight ? { background: 'linear-gradient(180deg,#2A2777,#242166)', borderColor: '#242166' } : {}}>
                                                    <div className="card-body" style={{ padding: '13px 15px' }}>
                                                        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: k.highlight ? '#B7B4EA' : '#9BA0B8' }}>{k.label}</div>
                                                        <div style={{ fontSize: 20, fontWeight: 750, marginTop: 6, color: k.highlight ? '#fff' : '#181A2C', fontVariantNumeric: 'tabular-nums' }}>
                                                            {k.money ? `${fmt(k.value)} F` : k.value}
                                                        </div>
                                                        {k.sub && <div style={{ fontSize: 11.5, marginTop: 3, color: k.highlight ? '#C9C7EE' : '#636A85' }}>{k.sub}</div>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Récaps groupés optionnels (par bénéficiaire, par taux d'impôt…) */}
                                        {allGroupRecaps.map((gr, gi) => {
                                            const groupes = (gr.compute(rows) || []);
                                            if (!groupes.length) return null;
                                            return (
                                                <Card key={gi} size="small" title={gr.title || "Récapitulatif"} style={{ marginBottom: 16, borderRadius: 12 }}>
                                                    <Table size="small" pagination={false} rowKey={(r) => r.label}
                                                        dataSource={groupes}
                                                        columns={[
                                                            { title: gr.labelHeader || 'Libellé', dataIndex: 'label' },
                                                            { title: 'Nombre', dataIndex: 'count', align: 'right', width: 120, render: (v) => <span className="num">{v}</span> },
                                                            { title: 'Total retenu (FCFA)', dataIndex: 'total', align: 'right', width: 180, render: (v) => <b style={{ color: '#242166' }}>{fmt(v)}</b> },
                                                        ]}
                                                        summary={() => (
                                                            <Table.Summary.Row style={{ fontWeight: 700, background: '#fafafc' }}>
                                                                <Table.Summary.Cell index={0}>Total général</Table.Summary.Cell>
                                                                <Table.Summary.Cell index={1} align="right">{groupes.reduce((a, g) => a + (g.count || 0), 0)}</Table.Summary.Cell>
                                                                <Table.Summary.Cell index={2} align="right">{fmt(groupes.reduce((a, g) => a + (g.total || 0), 0))}</Table.Summary.Cell>
                                                            </Table.Summary.Row>
                                                        )}
                                                    />
                                                </Card>
                                            );
                                        })}

                                        {/* Détail */}
                                        <div className="card">
                                            <div className="card-body">
                                                <Spin spinning={loading}>
                                                    <Table
                                                        dataSource={rows}
                                                        columns={columns}
                                                        rowKey={(r, i) => r.codeId || r.contratCodeId || i}
                                                        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `Total: ${t} ligne(s)` }}
                                                        scroll={{ x: 900 }}
                                                        size="small"
                                                        locale={{ emptyText: `Aucune donnée pour ${moisLabel}` }}
                                                    />
                                                </Spin>
                                            </div>
                                        </div>
                                    </>
                                )}

                            </div>
                            <Footer />
                        </div>
                    </div>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default FiscaliteScreen;
export { fmt, fetchAllPaginated };
